import { findAumSystemAttribute } from "$root/functional-areas/customAttributes/aum";
import type { CustomAttributeDto } from "$root/functional-areas/customAttributes/type";
import { z } from "zod";

type QuantityModeAumValues = {
	quantityMode: boolean;
	customAttributes?: Array<{ attributeUuid?: string | null; numberValue?: number | null } | undefined>;
};

export const AUM_REQUIRED_MESSAGE = "Enter the portfolio AUM to create it in quantities";
export const AUM_POSITIVE_MESSAGE = "The AUM of a portfolio in quantities must be greater than zero";

/**
 * Zod `superRefine` for the Main Info form: a portfolio created in quantities needs an AUM greater
 * than zero, because Core sizes the whole quantities to it. The issue goes on the AUM entry's
 * `numberValue`, where the AUM field shows it. Matches nothing in weights, or while the tenant's
 * attributes are unknown or have no AUM.
 */
export function refineQuantityModeAum(
	availableAttributes: CustomAttributeDto[] | undefined,
): (values: QuantityModeAumValues, ctx: z.RefinementCtx) => void {
	const aumAttributeUuid = findAumSystemAttribute(availableAttributes)?.attributeUuid;
	return (values, ctx) => {
		if (!values.quantityMode || aumAttributeUuid == null) {
			return;
		}
		const index = (values.customAttributes ?? []).findIndex((value) => value?.attributeUuid === aumAttributeUuid);
		const aum = index < 0 ? null : values.customAttributes?.[index]?.numberValue;
		if (aum != null && aum > 0) {
			return;
		}
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: index < 0 ? ["quantityMode"] : ["customAttributes", index, "numberValue"],
			message: aum == null ? AUM_REQUIRED_MESSAGE : AUM_POSITIVE_MESSAGE,
		});
	};
}
